import React, { useEffect, useState, useMemo, useCallback } from "react";
// eslint-disable-next-line import/no-unresolved
import Notification from "pages/_components/Notification";
import Head from "pages/_components/Head";
import { resizableRoute } from "pages/_components/Resizable";
import { Button, CreateDirectDebit, Box, Spinner } from "@ui-kit/components/index.es";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";
import yup from "yup";
import { selectors as sessionSelectors } from "reducers/session";
import { actions as directDebitActions, selectors as directDebitSelectors } from "reducers/directDebit";
import { actions as notificationActions } from "reducers/notification";
import * as i18n from "util/i18n";
import PropTypes from "prop-types";
import * as configUtils from "util/config";
import { downloadUrl, downloadTxt } from "util/download";
import * as accountUtils from "util/accounts";
import { selectors as statusSelectors } from "reducers/status";
import { useEmbeddedV3ShellLayoutSync } from "util/v3ShellLayoutSync";

const NewDirectDebit = (props) => {
    const [mappedAccounts, setMappedAccounts] = useState([]);
    const [currencySelected, setCurrencySelected] = useState({});
    const [formikProps, setFormikProps] = useState({});

    const {
        isDesktop,
        dispatch,
        activeEnvironment,
        accounts,
        payrollOptions,
        accessToken,
        isFetching,
        initialValues,
        lastPathname,
        embeddedInV3Shell,
    } = props;

    const cameFromDashboard = lastPathname === "/v3/dashboard";
    const goBackPath = cameFromDashboard ? "/v3/dashboard" : "/v2/directDebit/list";

    const onChangeAccount = (creditAccount) => {
        if (creditAccount) {
            const selectedAccount = accounts.find((account) => account.cbu === creditAccount);
            if (selectedAccount && selectedAccount?.currency !== currencySelected) {
                formikProps.setFieldValue("file", null);
                setCurrencySelected(selectedAccount.currency);
            }
        } else {
            formikProps.setFieldValue("file", null);
            setCurrencySelected({});
        }
    };

    const filesTypesToAccept = [".csv", "text/csv", "application/vnd.ms-excel", "text/x-csv"];

    useEffect(() => {
        setMappedAccounts(
            accounts?.map((account) => {
                const { productAlias, number: accountNumber, productType, currency, cbu } = account || {};
                return {
                    accountName: `${productAlias || ""} ${accountUtils.maskedAccountNumber(accountNumber || "")}`,
                    amount: account?.balance || 0,
                    currency: i18n.get(`currency.label.${currency || ""}`),
                    description: i18n.get(`extract.type.${productType || ""}`),
                    id: cbu || "",
                };
            }) || [],
        );
    }, [accounts]);

    useEffect(() => {
        dispatch(directDebitActions.directDebitCreatePre());
    }, []);

    const handleBack = () => {
        dispatch(push(goBackPath));
    };

    const handleClickDownload = useCallback(() => {
        const templateCsv = configUtils.get("url.directDebitRequest.samplefile");
        downloadUrl(templateCsv || "");
    }, []);

    const createShellRightContentMobile = useMemo(() => {
        if (!embeddedInV3Shell || !activeEnvironment?.permissions["directDebit.manage"]) {
            return null;
        }
        return (
            <Button
                icon="SendDown"
                iconVariant="outline"
                variant="text"
                color="white"
                onClick={handleClickDownload}
                isInactive={false}
                loading={false}
                aria-label={i18n.get("global.download")}
                className="icon-1-2"
            />
        );
    }, [embeddedInV3Shell, activeEnvironment?.permissions, handleClickDownload]);

    useEmbeddedV3ShellLayoutSync(dispatch, {
        embeddedInV3Shell,
        isDesktop,
        titleKey: "create.directDebit.title",
        goBackPath,
        scopes: ["directDebit"],
        rightContentMobile: createShellRightContentMobile,
    });

    const handleSubmit = (valuesForm) => {
        const selectedAccount = accounts.find((account) => account.cbu === valuesForm.creditAccount);
        dispatch(
            directDebitActions.directDebitPreviewRequest({
                ...valuesForm,
                productId: selectedAccount?.idProduct,
            }),
        );
    };
    const maxFileSize = configUtils.getInteger("core.maxFileSize");

    return (
        <>
            {!embeddedInV3Shell && (
                <Notification scopeToShow="directDebit" notificationClassname={!isDesktop ? "z-index-9" : ""} />
            )}
            <div className={`admin-detail-head ${isDesktop ? "px-4" : "d-block"}`}>
                {isDesktop && <Head onBack={handleBack} />}
                {!(embeddedInV3Shell && !isDesktop) && (
                    <Head
                        accessibilityTextId="create.directDebit.title"
                        headerClassName={
                            !isDesktop ? "blue-main-header-mobile blue-main-title-mobile background-transparent" : ""
                        }
                        title="create.directDebit.title"
                        titleClassName={
                            !isDesktop
                                ? "header-mobile-title-background-blue white-space-normal align-center"
                                : undefined
                        }
                        onBack={!isDesktop && handleBack}
                        rightContent={() => {
                            if (activeEnvironment?.permissions["directDebit.manage"]) {
                                return (
                                    <Button
                                        icon="SendDown"
                                        iconVariant="outline"
                                        variant={isDesktop ? "outline" : "solid"}
                                        onClick={handleClickDownload}
                                        isInactive={false}
                                        loading={false && isDesktop}
                                        aria-label={i18n.get("global.download")}
                                        text={isDesktop ? i18n.get("directDebit.download.example") : undefined}
                                        w={isDesktop ? "200px" : undefined}
                                        className="icon-1-2"
                                    />
                                );
                            }
                            return null;
                        }}
                        messageTooltip={isDesktop ? "frontend.DirectDebitRequest.TooltipHeader" : undefined}
                        hintText={isDesktop ? configUtils.get("frontend.DirectDebitRequest.TooltipHeader") : undefined}
                        alwaysShowMessage={isDesktop}
                    />
                )}
                {isFetching ? (
                    <Box flex justify="center" align="center" column py={72}>
                        <Spinner size="large" />
                    </Box>
                ) : (
                    <Box mt={isDesktop ? 32 : 0}>
                        <CreateDirectDebit
                            initialValues={{
                                creditAccount:
                                    initialValues?.creditAccount ||
                                    accounts?.find((account) => account.favorite)?.cbu ||
                                    accounts?.[0]?.cbu ||
                                    "",
                                reference: initialValues?.reference || "",
                                payrollsNumber: initialValues?.payrollsNumber || "",
                                file: initialValues?.file || null,
                            }}
                            onSubmit={handleSubmit}
                            validationSchema={yup.object().shape({
                                creditAccount: yup.string().required(i18n.get("transfers.validation.required")),
                                reference: yup
                                    .string()
                                    .required(i18n.get("transfers.validation.required"))
                                    .min(3, `${i18n.get("forms.validation.min.required")} de 3 caracteres`),
                                payrollsNumber: yup.string().required(i18n.get("transfers.validation.required")),
                                file: yup.object().required(i18n.get("transfers.validation.required")),
                            })}
                            onChangeAccount={onChangeAccount}
                            maxFileSize={maxFileSize}
                            loading={isFetching}
                            getFormLabel={(id) => i18n.get(`forms.directDebit.${id}`)}
                            accountsOptions={mappedAccounts}
                            payrollsNumberOptions={payrollOptions}
                            idActivity="direct.debit.files.upload"
                            accessToken={accessToken}
                            idEnvironment={activeEnvironment?.id}
                            setFormikProps={setFormikProps}
                            lang="es"
                            channel="frontend"
                            filesTypesToAccept={filesTypesToAccept}
                            onFileProcessFinished={(idFile, message, validLines, invalidLines, totalLines) => {
                                if (validLines === totalLines) {
                                    dispatch(
                                        notificationActions.showNotification(
                                            i18n.get(`directDebit.uploadFile.success`, null, {
                                                VALID_LINES: validLines,
                                                TOTAL_LINES: totalLines,
                                            }),
                                            "success",
                                            ["directDebit"],
                                            null,
                                            false,
                                        ),
                                    );
                                }
                                if (invalidLines > 0) {
                                    dispatch(
                                        notificationActions.showNotification(
                                            i18n.get("directDebit.uploadFile.error.lines"),
                                            "error",
                                            ["directDebit"],
                                            null,
                                            false,
                                        ),
                                    );
                                }
                            }}
                            onFileProcessError={(error) => {
                                dispatch(
                                    notificationActions.showNotification(
                                        error?.NO_FIELD || i18n.get("directDebit.uploadFile.error"),
                                        "error",
                                        ["directDebit"],
                                        null,
                                        false,
                                    ),
                                );
                            }}
                            hintText={
                                !isDesktop ? configUtils.get("frontend.DirectDebitRequest.TooltipHeader") : undefined
                            }
                            downloadTxt={downloadTxt}
                        />
                    </Box>
                )}
            </div>
        </>
    );
};

NewDirectDebit.propTypes = {
    isDesktop: PropTypes.bool,
    dispatch: PropTypes.func,
    activeEnvironment: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        permissions: PropTypes.objectOf(PropTypes.any),
    }),
    accounts: PropTypes.arrayOf(PropTypes.object),
    payrollOptions: PropTypes.arrayOf(PropTypes.object),
    accessToken: PropTypes.string,
    isFetching: PropTypes.bool,
    initialValues: PropTypes.shape({
        creditAccount: PropTypes.string,
        reference: PropTypes.string,
        payrollsNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        file: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    }),
    lastPathname: PropTypes.string,
    embeddedInV3Shell: PropTypes.bool,
};
NewDirectDebit.defaultProps = {
    isDesktop: false,
    dispatch: () => {},
    activeEnvironment: {
        permissions: {},
    },
    accounts: [],
    payrollOptions: [],
    accessToken: "",
    isFetching: false,
    initialValues: null,
    lastPathname: "",
    embeddedInV3Shell: false,
};

const mapStateToProps = (state) => ({
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    accounts: directDebitSelectors.getAccounts(state),
    isFetching: directDebitSelectors.getFetchingList(state) || directDebitSelectors.getFetchingAccounts(state),
    accessToken: sessionSelectors.getAccessToken(state),
    payrollOptions: directDebitSelectors.getPayrollOptions(state),
    initialValues: directDebitSelectors.getInitialValues(state),
    lastPathname: statusSelectors.getPathname(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(NewDirectDebit);
